import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@Components/Button/Button';
import { Appearance, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Spinner } from '@Components/Spinner';
import { useSetError } from '@src/redux/modals';
import { GRANULARITY_OPTIONS, GranularityLevel } from './goal-intake-constants';
import {
  mediaTypeSettingsQueryKey,
  saveMediaTypeSettings,
  useMediaTypeSettings
} from './use-goal-intake';

interface GoalIntakeSettingsModalProps {
  company: string;
  onHide: () => void;
}

// Goal granularity per media type. Only mounted while open, so the settings load on demand.
export const GoalIntakeSettingsModal = ({ company, onHide }: GoalIntakeSettingsModalProps) => {
  const setError = useSetError();
  const queryClient = useQueryClient();
  const { data: savedSettings, isLoading, error } = useMediaTypeSettings(company);

  // Editable working copy, seeded once the saved settings load.
  const [settings, setSettings] = useState<Map<string, boolean>>(new Map());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (savedSettings) {
      setSettings(savedSettings);
    }
  }, [savedSettings]);

  useEffect(() => {
    if (error) {
      setError({
        message: `Failed to get Campaigns Data. ERROR: ${error.message}`,
        reportError: error
      });
      onHide();
    }
  }, [error, setError, onHide]);

  const hasChanges = useMemo(
    () =>
      savedSettings !== undefined &&
      Array.from(settings).some(([mediaType, value]) => savedSettings.get(mediaType) !== value),
    [settings, savedSettings]
  );

  const save = async () => {
    setSaving(true);
    try {
      await saveMediaTypeSettings(
        company,
        Array.from(settings).map(([mediaType, isMediaTypeActive]) => ({
          mediaType,
          isMediaTypeActive
        }))
      );
      await queryClient.invalidateQueries({ queryKey: mediaTypeSettingsQueryKey(company) });
      onHide();
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show
      onHide={onHide}
      className='goal-intake-settings-modal'
      data-testid='goal-intake-settings-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>Goal Granularity Settings</Modal.Title>
      </Modal.Header>
      <Modal.Body className='goal-intake-settings-body'>
        {isLoading ? (
          <Spinner />
        ) : (
          Array.from(settings).map(([mediaType, isCampaignLevel]) => (
            <div className='settings-row' key={mediaType}>
              <div className='settings-media-type'>{mediaType}</div>
              <Dropdown
                size={Size.LG}
                options={GRANULARITY_OPTIONS}
                value={isCampaignLevel ? GranularityLevel.CAMPAIGN : GranularityLevel.SEGMENT}
                onChange={option =>
                  setSettings(prev =>
                    new Map(prev).set(mediaType, option === GranularityLevel.CAMPAIGN)
                  )
                }
              />
            </div>
          ))
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={onHide}>
          Cancel
        </Button>
        <Button size={Size.LG} disabled={!hasChanges} loading={saving} onClick={save}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
